import { useState } from 'react';
import type { ReactElement } from 'react';
import { useAllowCustomerMutation, useCustomersQuery } from '@/api/endpoints';
import type { Customer } from '@/api/types';
import { useCan } from '@/app/session';
import { Button } from '@/design/Button';
import { Identifier } from '@/design/Data';
import { DataTable } from '@/design/DataTable';
import type { Column } from '@/design/DataTable';
import { Field, TextInput } from '@/design/Field';
import { Panel } from '@/design/Panel';
import { StatusPill } from '@/design/StatusPill';
import { useToast } from '@/design/Toast';
import { BlacklistModal } from './BlacklistModal';

/**
 * Customers.
 *
 * Lifted off the booking form, where the list of everybody who has ever rented
 * was buried behind a search box. It is a screen of its own because two
 * questions get asked of it that a search box cannot answer: who are our repeat
 * customers, and who must we not rent to again.
 *
 * The blacklist is the reason this screen exists. It is not a deletion and not
 * a hidden flag — the row stays, the bookings stay, and the reason is on the
 * screen for whoever picks up the phone next, which may be somebody who was not
 * there when it happened.
 */

function phoneDigits(phone: string): string {
  return phone.replace(/[^\d+]/g, '');
}

export function CustomersPage(): ReactElement {
  const [search, setSearch] = useState('');
  const [showing, setShowing] = useState<'all' | 'only'>('all');
  const [toBlacklist, setToBlacklist] = useState<Customer | null>(null);

  const query = useCustomersQuery({
    ...(search.trim().length >= 2 ? { q: search.trim() } : {}),
    ...(showing === 'only' ? { blacklisted: 'only' as const } : {}),
  });
  const [allowCustomer, allowing] = useAllowCustomerMutation();
  const canBlacklist = useCan('bookings.cancel');
  const toast = useToast();

  async function allow(customer: Customer): Promise<void> {
    try {
      await allowCustomer(customer.id).unwrap();
      toast.done(`${customer.name} can book again.`);
    } catch {
      toast.problem(`${customer.name} was not taken off the list. Try again.`);
    }
  }

  const columns: readonly Column<Customer>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (customer) => (
        <div className="flex flex-col">
          <span className={customer.is_blacklisted === true ? 'font-semibold' : undefined}>
            {customer.name}
          </span>
          {/* The reason travels with the name, because the name is what a staff
              member is looking at when the customer is on the phone. */}
          {customer.is_blacklisted === true && customer.blacklist_reason !== '' && (
            <span className="text-small text-alert">{customer.blacklist_reason}</span>
          )}
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (customer) => (
        <a
          href={`tel:${phoneDigits(customer.phone)}`}
          className="identifier underline-offset-2 hover:underline"
        >
          {customer.phone}
        </a>
      ),
    },
    {
      // The second identity a blacklisting is matched on, so it belongs on the
      // screen where blacklisting happens. A phone number is a SIM card away
      // from being somebody else; a licence is not.
      key: 'licence',
      header: 'Licence',
      render: (customer) =>
        customer.licence_no === undefined || customer.licence_no === '' ? (
          <span className="text-ink-faint">—</span>
        ) : (
          <Identifier>{customer.licence_no}</Identifier>
        ),
    },
    {
      key: 'bookings',
      header: 'Bookings',
      numeric: true,
      render: (customer) => <span className="tnum">{customer.booking_count ?? 0}</span>,
    },
    {
      key: 'standing',
      header: 'Status',
      render: (customer) => (
        <StatusPill
          status={
            customer.is_blacklisted === true
              ? { label: 'Blacklisted', tone: 'alert', shape: 'cross' }
              : { label: 'Fine', tone: 'active', shape: 'square' }
          }
        />
      ),
    },
    {
      key: 'action',
      header: '',
      render: (customer) =>
        !canBlacklist ? null : customer.is_blacklisted === true ? (
          <Button onClick={() => void allow(customer)} disabled={allowing.isLoading}>
            Allow again
          </Button>
        ) : (
          <Button intent="danger" onClick={() => setToBlacklist(customer)}>
            Blacklist
          </Button>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-display font-semibold">Customers</h1>

      <Panel
        title="Everyone who has rented"
        note="Search by name, phone number or licence"
        current
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              intent={showing === 'all' ? 'primary' : 'secondary'}
              onClick={() => setShowing('all')}
            >
              Everyone
            </Button>
            <Button
              intent={showing === 'only' ? 'primary' : 'secondary'}
              onClick={() => setShowing('only')}
            >
              Blacklisted only
            </Button>
          </div>
        }
      >
        <div className="border-b border-rule px-3 py-3 sm:px-4">
          <Field label="Search">
            {({ id, describedBy }) => (
              <TextInput id={id} describedBy={describedBy} value={search} onChange={setSearch} />
            )}
          </Field>
        </div>

        <DataTable
          columns={columns}
          rows={query.data?.results ?? []}
          rowKey={(customer) => customer.id}
          isLoading={query.isLoading}
          isError={query.isError}
          onRetry={() => void query.refetch()}
          errorHeadline="The customers did not load"
          errorDetail="The server did not answer. Check the connection and load them again."
          empty={{
            headline:
              showing === 'only'
                ? 'Nobody is blacklisted'
                : search === ''
                  ? 'No customers yet'
                  : 'Nobody matches that',
            detail:
              showing === 'only'
                ? 'Blacklisting somebody records the reason and refuses them further bookings. Nothing is deleted.'
                : search === ''
                  ? 'A customer is created the first time somebody rents. Take a booking and they will appear here.'
                  : 'Try fewer digits, or part of the name.',
          }}
        />
      </Panel>

      <BlacklistModal customer={toBlacklist} onClose={() => setToBlacklist(null)} />
    </div>
  );
}
